import * as React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { Info } from "lucide-react";

import { FOCUS_RING } from "../lib/focus";
import { HIT_AREA } from "../lib/target";
import { cn } from "../lib/utils";
import { Icon } from "./icon";

/**
 * v2 Tooltip (redesign phase 5, #455). Storybook only until implementation;
 * see docs/design-library/04-implementation-notes.md for the swap.
 *
 * A tooltip is a LABEL, not a popover: text only, never links or buttons
 * inside. It opens on hover (after the provider's delay) and on keyboard focus
 * (at once); Escape, blur and pointer-down close it. The content stays
 * hoverable (WCAG 1.4.13).
 *
 * Surface: the inverse of the page, `bg-foreground text-background`, so it is
 * light in dark mode (break #24). No border, no shadow, no arrow.
 *
 * Motion: a 150ms fade-in through `@starting-style` (Tailwind core `starting:`,
 * no animation plugin). None when moving between tooltips (`instant-open`) or
 * under reduced motion. Closing is instant: Radix Presence only waits for CSS
 * animations, not transitions.
 */

/**
 * Mount ONCE at the app root (Storybook: global decorator). One provider is
 * what lets a row of icon buttons skip the delay after the first tooltip.
 * Every v2 Tooltip, and so every v2 IconButton, needs it: Radix throws without.
 */
function TooltipProvider({
  delayDuration = 400,
  skipDelayDuration = 300,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return <TooltipPrimitive.Provider delayDuration={delayDuration} skipDelayDuration={skipDelayDuration} {...props} />;
}

const Tooltip = TooltipPrimitive.Root;

const TooltipTrigger = TooltipPrimitive.Trigger;

export type TooltipSide = "top" | "right" | "bottom" | "left";

/**
 * `default` 240 (a label). `wide` 320 for a sentence of 150–190 characters
 * (MachineScopeMarker): 3–4 lines instead of 5–6. Wide is capped to the
 * viewport through Radix's available width (an Outlook pane can be 320), as
 * an inline style: the one allowed use (redesign-brief.md, Rules).
 */
export type TooltipWidth = "default" | "wide";

const WIDE_STYLE: React.CSSProperties = { maxWidth: "min(20rem, var(--radix-tooltip-content-available-width))" };

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content> & { width?: TooltipWidth }
>(function TooltipContent(
  { className, side = "top", sideOffset = 6, collisionPadding = 8, width = "default", style, ...props },
  ref,
) {
  return (
    // Portalled so table and card `overflow` can't clip it. Tokens and `.dark`
    // sit on <html>, so the portal inherits them.
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        ref={ref}
        side={side}
        sideOffset={sideOffset}
        collisionPadding={collisionPadding}
        data-width={width}
        style={width === "wide" ? { ...WIDE_STYLE, ...style } : style}
        className={cn(
          "z-50 rounded-md bg-foreground px-2 py-1 font-sans text-caption break-words text-background",
          width === "default" && "max-w-60",
          "transition-opacity duration-150 starting:opacity-0 data-[state=instant-open]:transition-none motion-reduce:transition-none",
          className,
        )}
        {...props}
      />
    </TooltipPrimitive.Portal>
  );
});

export interface PinnableTooltipProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "content" | "type" | "onClick" | "onBlur"> {
  /** The tooltip text. Text only. */
  content: React.ReactNode;
  side?: TooltipSide;
  width?: TooltipWidth;
  /** Start pinned open (static demos in stories). */
  defaultPinned?: boolean;
  /** The trigger's visible content; the trigger itself is a `<button type="button">`. */
  children: React.ReactNode;
}

/**
 * A tooltip on its own button trigger that a click or tap PINS open (and a
 * second click unpins): people click explanations, and touch has no hover. A
 * plain toggle would flash, because the click's focus would open and the toggle
 * close it again. Hover and focus open it like any tooltip; Escape, blur and a
 * pointer-down elsewhere unpin. Shared by InfoTooltip and ConfidenceBadge; the
 * caller styles the trigger.
 */
const PinnableTooltip = React.forwardRef<HTMLButtonElement, PinnableTooltipProps>(function PinnableTooltip(
  { content, side, width, defaultPinned = false, children, ...props },
  ref,
) {
  const [hoverOpen, setHoverOpen] = React.useState(false);
  const [pinned, setPinned] = React.useState(defaultPinned);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  React.useImperativeHandle(ref, () => triggerRef.current as HTMLButtonElement);

  const unpin = () => setPinned(false);

  return (
    <Tooltip open={pinned || hoverOpen} onOpenChange={setHoverOpen}>
      <TooltipTrigger
        ref={triggerRef}
        type="button"
        {...props}
        onClick={(event) => {
          // Skips Radix's close-on-click; pinning decides instead.
          event.preventDefault();
          if (pinned) {
            setPinned(false);
            setHoverOpen(false);
          } else {
            setPinned(true);
          }
        }}
        onBlur={unpin}
      >
        {children}
      </TooltipTrigger>
      <TooltipContent
        side={side}
        width={width}
        onEscapeKeyDown={unpin}
        onPointerDownOutside={(event) => {
          // The trigger counts as "outside" the content; its own click handles it.
          if (!triggerRef.current?.contains(event.target as Node)) unpin();
        }}
      >
        {content}
      </TooltipContent>
    </Tooltip>
  );
});

export interface InfoTooltipProps {
  /** Accessible name of the icon button, e.g. "Mehr Informationen". German UI text. */
  label: string;
  /** The tooltip text. Text only. */
  children: React.ReactNode;
  /** 12 next to caption text (table headers), 16 next to 14px text. */
  size?: 12 | 16;
  side?: TooltipSide;
  className?: string;
}

/**
 * Info icon that explains the thing next to it (was Venus HelpTooltip, break #37).
 * Place it NEXT TO a `<label>`, never inside one (the click would reach the field).
 * Hover and focus open it; a click or tap pins it (PinnableTooltip).
 */
function InfoTooltip({ label, children, size = 16, side, className }: InfoTooltipProps) {
  return (
    <PinnableTooltip
      aria-label={label}
      side={side}
      content={children}
      // `-m-1` cancels `p-1`: the box grows (24 at 16, 20 at 12) without moving the layout; HIT_AREA tops 12 up to 24.
      className={cn(
        "-m-1 inline-flex shrink-0 cursor-default rounded-sm p-1 align-middle text-ink-muted transition-colors hover:text-ink",
        FOCUS_RING,
        HIT_AREA,
        className,
      )}
    >
      <Icon icon={Info} size={size} />
    </PinnableTooltip>
  );
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider, InfoTooltip, PinnableTooltip };
